import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  convertChartsToImagesAndHideThem,
  removeHiddenChartImages,
  convertAWSSourcedImages,
  removeImagesWithImgType,
  formatDocx,
} from "@shared/components/EditorJs/utils/reportDownloading";
import ShareMenu from "@shared/components/ShareProject/ShareProjectModal";
import { Member } from "@shared/interfaces/User";
import AvatarList from "@pages/Home/components/AvatarList";
import { useReport } from "@utils/useReports";
import { ReportHeaderProps } from "@shared/interfaces/ReportsFiles";
import PublishConfirmationModal from "./PublishConfirmationModal";
import { toastService } from "@services/ToastService";
import { useUser } from "@services/UserContext";
import { displayReportName } from "@pages/Project/utils/utils";

type AxiosLike = {
  data: Blob | ArrayBuffer;
  headers?: Record<string, string>;
};

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  toggleEdit,
  isEditing,
  saveChanges,
  discardChanges,
  sharedToken,
  isReportViewer,
  canEdit,
  canShare,
  canViewProjectFiles,
  report,
}) => {
  const navigate = useNavigate();
  const {
    public_accessible: publicAccess,
    public_id: reportPublicId,
    published: reportPublished,
    name: reportName,
    creator: reportCreator,
  } = report;

  const { projectId, reportId } = useParams<{
    projectId: string;
    reportId: string;
  }>();

  const { user } = useUser();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [users, setUsers] = useState<Member[]>([]);
  const [isReducedModal, setIsReducedModal] = useState(false);

  const shareMenuRef = useRef<HTMLDivElement | null>(null);
  const memberButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleMemberUpdate = (updatedMembers: Member[]) => {
    setUsers(updatedMembers);
  };

  const dropdownRef = useRef<HTMLDivElement>(null);
  const { exportReport, fetchReportMembers, publishReport } = useReport();

  const [isModalOpen, setModalOpen] = useState(false);
  const [reportPublishedState, setReportPublishedState] =
    useState(reportPublished);

  const handlePublish = async () => {
    let success = false;
    if (!reportId || !projectId) return;

    if (reportId) {
      try {
        success = await publishReport(projectId, reportId, true);
        if (success) {
          toastService.success("Report published successfully!");
          setModalOpen(false);
          setReportPublishedState(true);
        } else {
          console.error("Failed to update published status");
        }
      } catch (error) {
        console.error("Error updating published status:", error);
      }
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  function triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function extractBlobAndHeaders(
    response: Response | AxiosLike
  ): Promise<{ blob: Blob }> {
    // fetch Response
    if (
      "headers" in response &&
      typeof (response as Response).blob === "function"
    ) {
      const res = response as Response;
      const blob = await res.blob();
      return { blob };
    }

    // axios-like
    const axiosRes = response as AxiosLike;
    const headers = Object.fromEntries(
      Object.entries(axiosRes.headers || {}).map(([k, v]) => [
        k.toLowerCase(),
        v,
      ])
    );

    const isBlob = axiosRes.data instanceof Blob;
    const blob = isBlob
      ? (axiosRes.data as Blob)
      : new Blob([axiosRes.data as ArrayBuffer], {
          type: headers["content-type"] || "application/octet-stream",
        });

    return { blob };
  }

  const downloadReportDocx = async () => {
    const element = document.getElementById("custom-editor");
    if (!element) return;

    await Promise.all([
      convertChartsToImagesAndHideThem(element),
      convertAWSSourcedImages(element),
      removeImagesWithImgType(element),
    ]);

    const clonedElement = element.cloneNode(true) as HTMLElement;
    formatDocx(clonedElement);

    const cssStyles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules || [])
            .filter((rule) => {
              if (rule instanceof CSSStyleRule) {
                try {
                  return (
                    clonedElement.querySelector(rule.selectorText) !== null
                  );
                } catch {
                  return false;
                }
              }
              return false;
            })
            .map((rule) => rule.cssText)
            .join("\n");
        } catch (e) {
          // Skip any cross-origin stylesheets (they will throw errors)
          return "";
        }
      })
      .join("\n");

    const HtmlStr = `<!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta http-equiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
      <meta http-equiv="Pragma" content="no-cache" />
      <meta http-equiv="Expires" content="0" />
      <title>testTitle</title>
      <style>
        ${cssStyles}
      </style>
    </head>
    <body>
        ${clonedElement.outerHTML}
    </body>
    </html>`;

    try {
      if (!projectId || !reportId) {
        console.error("Project ID or Report ID is missing.");
        return;
      }

      const response = await exportReport(projectId, reportId, HtmlStr);

      // Unify fetch/axios handling
      const { blob } = await extractBlobAndHeaders(response);

      const fileName =
        `${displayReportName(
          report.name,
          report.template_name,
          report.created_at
        )}.pdf` || `report_${reportId}.pdf`;

      triggerBlobDownload(blob, fileName);

      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error exporting report:", error);
    } finally {
      removeHiddenChartImages(element);
    }
  };

  const fetchMembers = async () => {
    try {
      if (projectId && reportId) {
        const data = await fetchReportMembers(projectId, reportId);
        setUsers(data);
      } else {
        console.error("Project ID or Report ID is missing.");
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const handleShareButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsShareMenuOpen((prev) => !prev);
    setIsReducedModal(false);
  };

  const handleMemberButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsShareMenuOpen((prev) => !prev);
    setIsReducedModal(true);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      shareMenuRef.current &&
      !shareMenuRef.current.contains(event.target as Node)
    ) {
      setIsShareMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    // Cleanup the event listener when unmounting
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (canShare) fetchMembers();
  }, [projectId, reportId, canShare]);

  return (
    <>
      {(canShare || canEdit || canViewProjectFiles) && (
        <div
          className="flex justify-between items-center h-[4.5rem] p-4 bg-white border-b-2 border-gray-100 max-w-full"
          id="reportHeaderDiv"
        >
          <div>
            {canViewProjectFiles && (
              <button
                onClick={() =>
                  navigate(
                    `/project/${projectId}${
                      sharedToken ? `/token/${sharedToken}` : "?tab=Reports"
                    }`
                  )
                }
                className="text-blue-600 text-sm 2xl:text-base flex items-center space-x-2"
              >
                <span>
                  <img src="/arrow-left.svg" className="w-5 h-5" alt="" />
                </span>
                <span>Back to Reports</span>
              </button>
            )}
          </div>
          <div className="flex space-x-4">
            {isEditing ? (
              <>
                <button
                  onClick={discardChanges}
                  className="bg-white border-blue-500 border-2 text-blue-500 px-4 py-2 rounded-md hover:bg-blue-500 hover:text-white hover:border-2 transition"
                >
                  Discard
                </button>
                <button
                  onClick={saveChanges}
                  className="bg-blue-500 text-white border-2 border-blue-500 px-4 py-2 rounded-md hover:bg-white hover:text-blue-500 hover:border-2 hover:border-blue-500 transition"
                >
                  Save changes
                </button>
              </>
            ) : isReportViewer === false ? (
              <>
                <div className="flex items-center space-x-4">
                  <div className="relative flex space-x-4">
                    <span ref={memberButtonRef}>
                      <AvatarList
                        shared_with={users}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMemberButtonClick(e);
                        }}
                        size={10}
                      />
                    </span>

                    {canShare && (
                      <button
                        onClick={handleShareButtonClick}
                        className="text-blue-600 font-medium text-sm 2xl:text-base"
                      >
                        Share
                      </button>
                    )}
                    <div
                      ref={shareMenuRef}
                      className="absolute right-0 bottom-0"
                    >
                      <ShareMenu
                        isOpen={isShareMenuOpen}
                        projectId={projectId || ""}
                        reportId={reportId || ""}
                        ownerFirstName={""}
                        ownerLastName={""}
                        isAdmin={canShare}
                        reducedVersion={isReducedModal}
                        onMemberUpdate={handleMemberUpdate}
                        publicAccess={publicAccess}
                        reportPublicId={reportPublicId}
                      />
                    </div>
                  </div>

                  {canEdit && (
                    <>
                      {!reportPublishedState ? (
                        <div className="relative">
                          <div
                            className="inline-flex rounded-md shadow-md"
                            role="group"
                          >
                            <button
                              type="button"
                              className="px-4 py-3 text-sm 2xl:text-base font-medium text-white bg-blue-600 rounded-s-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-400"
                              onClick={toggleEdit}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="px-4 py-3 text-sm 2xl:text-base font-medium text-white bg-blue-600 rounded-e-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsDropdownOpen((prev) => !prev);
                              }}
                            >
                              <img
                                src="/arrow_dropdown.svg"
                                className="w-3"
                                alt="Dropdown Arrow"
                              />
                            </button>
                          </div>

                          {isDropdownOpen && (
                            <div
                              ref={dropdownRef}
                              className="absolute right-0 mt-2 w-28 2xl:w-32 bg-white shadow-md rounded-md border z-50"
                            >
                              <button
                                onClick={downloadReportDocx}
                                className="w-full text-sm 2xl:text-base block text-left px-4 py-2 text-gray-800 hover:bg-gray-100"
                              >
                                Download
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="relative">
                          <button
                            type="button"
                            className="px-5 py-3  font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-400"
                            onClick={downloadReportDocx}
                          >
                            Download
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  <div className="relative group flex flex-col items-center justify-center">
                    {!reportPublishedState ? (
                      <>
                        {reportCreator.id !== user?.id && (
                          <div className="absolute top-full right-0 mt-2 bg-gray-800 p-2 rounded shadow-md hidden group-hover:block z-[99]">
                            <p className="text-xs text-gray-100 whitespace-nowrap">
                              Only the report creator can publish this report
                            </p>
                          </div>
                        )}
                        <button
                          type="button"
                          className="px-5 py-3 text-sm 2xl:text-base font-medium text-white bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
                          onClick={() => setModalOpen(true)}
                          disabled={reportCreator.id !== user?.id}
                        >
                          Publish
                        </button>
                      </>
                    ) : (
                      <div className="border-2 border-blue-600 hover:bg-blue-50 w-12 h-12 flex justify-center rounded-lg">
                        <div className="absolute top-full mt-2 right-0  bg-gray-800 p-2 rounded shadow-md hidden group-hover:block z-[99]">
                          <p className="text-xs text-gray-100 whitespace-nowrap">
                            This is a published report
                          </p>
                        </div>
                        <button type="button" className="py-1 mt-[-1.5px]">
                          <img
                            src="/secure.png"
                            className="h-[2.25rem] w-[2.25rem]"
                            alt=""
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
      <PublishConfirmationModal
        isOpen={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onPublish={handlePublish}
        reportName={reportName ?? ""}
      />
    </>
  );
};
